import { OptionsType } from '@openx/types/options.ts';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig.ts';
import { useAppInventoryFetch } from './utils/content/useAppInventoryFetch.ts';
import { useAppNameBulkValidationV2 } from './utils/content/useAppNameBulkValidationV2.ts';
import { useFetchInventoriesBySearchText } from './utils/content/useFetchInventoriesBySearchText.ts';
import { useFetchInventoryInstances } from './utils/content/useFetchInventoryInstances.ts';
import { useFetchInventoryMarket } from './utils/content/useFetchInventoryMarket.ts';
import { useInventoriesFetch } from './utils/content/useInventoriesFetch.ts';
import { usePmpSizesFetch } from './utils/content/usePmpSizesFetch.ts';
import { useResolveAppGroupIds } from './utils/content/useResolveAppGroupIds.ts';
import { useCountriesOptionsFetch } from './utils/geodata/useCountriesOptionsFetch.ts';
import { useFetchByGeoData } from './utils/geodata/useFetchByGeoData.ts';
import { useFetchBySearchText } from './utils/geodata/useFetchBySearchText.ts';
import { usePostalCodes } from './utils/geodata/usePostalCodes.ts';
import { useAdPlacementOptionsFetch } from './utils/inventoryFormat/useAdPlacementOptionsFetch.ts';
import { useDistributionChannelOptionsFetch } from './utils/inventoryFormat/useDistributionChannelOptionsFetch.ts';
import { useOldAdPlacementOptionsFetch } from './utils/inventoryFormat/useOldAdPlacementOptionsFetch.ts';
import { useOldDistributionChannelOptionsFetch } from './utils/inventoryFormat/useOldDistributionChannelOptionsFetch.ts';
import { useAccountOptionsFetch } from './utils/useAccountOptionsFetch.ts';
import { useAccountUid } from './utils/useAccountUid.tsx';
import { useAudienceCreateAndActivate } from './utils/useAudienceCreateAndActivate.ts';
import { useAudienceOptionsDetailsFetch } from './utils/useAudienceOptionsDetailsFetch.ts';
import { useAudienceOptionsDetailsLazyFetch } from './utils/useAudienceOptionsDetailsLazyFetch.ts';
import { useAudienceOptionsFetch } from './utils/useAudienceOptionsFetch.ts';
import { useCategoriesOptionsFetch } from './utils/useCategoriesOptionsFetch.ts';
import { useContentObjectOptionsConfig } from './utils/useContentObjectOptionsConfig.ts';
import { useContextualDataPartnerOptions } from './utils/useContextualDataPartnerOptions.ts';
import { useCurrentInstanceState } from './utils/useCurrentInstanceState.tsx';
import { useDeviceTypesOptionsFetch } from './utils/useDeviceTypesOptionsFetch.ts';
import { useFetchSegments } from './utils/useFetchSegments.ts';
import { useFetchSegmentsV2 } from './utils/useFetchSegmentsV2.ts';
import { useGetAudienceMultiproviderInfo } from './utils/useGetAudienceMultiproviderInfo.ts';
import { useGetContextualPricing } from './utils/useGetContextualPricing.ts';
import { useInventoryListByIdFetch } from './utils/useInventoryListByIdFetch.ts';
import { useInventoryListsOptionsFetch } from './utils/useInventoryListsOptionsFetch.ts';
import { useIsRetargetingAllowed } from './utils/useIsRetargetingAllowed.ts';
import { useKeywordsOptionsFetch } from './utils/useKeywordsOptionsFetch.ts';
import { useMetacategoriesFetch } from './utils/useMetacategoriesFetch.ts';
import { useRelatedAccountsFetch } from './utils/useRelatedAccountsFetch.ts';
import { useRetargetingSegmentDetailsFetch } from './utils/useRetargetingSegmentDetailsFetch.ts';
import { useRetargetingSegmentsOptionsFetch } from './utils/useRetargetingSegmentsOptionsFetch.ts';
import { useSelectRateOptionsFetch } from './utils/useSelectRateOptionsFetch.ts';
import { useTargetingBlockingRulesSchemaFetch } from './utils/useTargetingBlockingRulesSchemaFetch.ts';
import { useTechnologyOptionsFetch } from './utils/useTechonologyOptionsFetch.ts';
import { useVideoOptionsFetch } from './utils/useVideoOptionsFetch.ts';

export const targetingConfig: TargetingProviderConfig = {
  isOa: true,
  useAppInventoryFetch: useAppInventoryFetch,
  useAppNameBulkValidationV2: useAppNameBulkValidationV2,
  useResolveAppGroupIds: useResolveAppGroupIds,
  useAccountOptionsFetch,
  useAccountUid,
  useAdPlacementOptions: () => {
    const { mapById, loading, refetch } = useAdPlacementOptionsFetch();

    return { adPlacement: mapById, isAdPlacementLoading: loading, refetch };
  },
  useOldAdPlacementOptions: () => {
    const { mapById, loading, refetch } = useOldAdPlacementOptionsFetch();

    return { adPlacement: mapById, isAdPlacementLoading: loading, refetch };
  },
  useAudienceOptionsDetailsFetch,
  useAudienceOptionsDetailsLazyFetch,
  useAudienceCreateAndActivate,
  useAudienceOptionsFetch,
  useCategoriesFetch: useCategoriesOptionsFetch,
  useContentObjectOptionsConfig,
  useCountriesOptionsFetch,
  useCurrentInstanceState,
  useDeviceTypesOptionsFetch: () => {
    const deviceTypesOptions = useDeviceTypesOptionsFetch();

    return {
      [OptionsType.DESKTOP_DEVICES]: {
        data: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.MOBILE_DEVICES]: {
        data: deviceTypesOptions[OptionsType.MOBILE_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.MOBILE_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.MOBILE_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.TV_DEVICES]: {
        data: deviceTypesOptions[OptionsType.TV_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.TV_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.TV_DEVICES].refetch || (() => Promise.resolve()),
      },
    };
  },
  useDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  useOldDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useOldDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  useFetchByGeoData,
  useFetchBySearchText,
  useFetchInventoriesBySearchText,
  useFetchInventoryInstances,
  useFetchInventoryMarket,
  useFetchSegments,
  useFetchSegmentsV2,
  useContextualDataPartnerOptions,
  useInventoriesFetch,
  useKeywordsOptionsFetch,
  useMetacategoriesFetch,
  usePmpSizesFetch,
  usePostalCodes,
  useRelatedAccountsFetch,
  useRetargetingSegmentsOptionsFetch,
  useRetargetingSegmentDetailsFetch,
  useIsRetargetingAllowed,
  useSelectRateOptionsFetch,
  useTechnologyOptionsFetch,
  useVideoOptionsFetch,
  useInventoryListsOptionsFetch,
  useInventoryListByIdFetch,
  useGetAudienceMultiproviderInfo,
  useGetContextualPricing,
  useTargetingBlockingRulesSchemaFetch,
};
